import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, Save, X, GitMerge } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { EmptyState } from '@/components/EmptyState';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { useLabel } from '@/lib/labels';

/** `/settings/suppliers-lite` (v1.0 Phase 3 Sprint 9、Lite版 仕入先マスタ)。
 *  AccountCategoriesSettingsPage.tsx と対のページ (同一パターン)。 */
type Supplier = { id: string; code: string; name: string; is_system: boolean; notes: string | null };
type EditingRow = { id: string; name: string };

export function SuppliersLiteSettingsPage() {
  const t = useLabel();
  const qc = useQueryClient();
  const { data: suppliers = [], isLoading } = useQuery<Supplier[]>({
    queryKey: ['suppliers-lite'],
    queryFn: () => apiFetch<Supplier[]>('/suppliers-lite'),
  });

  const [creating, setCreating] = useState<{ code: string; name: string } | null>(null);
  const [editing, setEditing] = useState<EditingRow | null>(null);
  const [deleting, setDeleting] = useState<Supplier | null>(null);
  const [mergeSelection, setMergeSelection] = useState<Set<string>>(new Set());
  const [merging, setMerging] = useState<{ new_code: string; new_name: string } | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['suppliers-lite'] });

  const createMut = useMutation({
    mutationFn: (body: { code: string; name: string }) => apiFetch('/suppliers-lite', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => { invalidate(); setCreating(null); toast.success('仕入先を追加しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '追加に失敗しました'),
  });
  const updateMut = useMutation({
    mutationFn: (r: EditingRow) => apiFetch(`/suppliers-lite/${r.id}`, { method: 'PATCH', body: JSON.stringify({ name: r.name.trim() }) }),
    onSuccess: () => { invalidate(); setEditing(null); toast.success('仕入先を更新しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '更新に失敗しました'),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/suppliers-lite/${id}`, { method: 'DELETE' }),
    onSuccess: () => { invalidate(); setDeleting(null); toast.success('仕入先を削除しました'); },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'); setDeleting(null); },
  });
  const mergeMut = useMutation({
    mutationFn: (body: { source_ids: string[]; new_code: string; new_name: string }) =>
      apiFetch('/suppliers-lite/merge', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => { invalidate(); setMerging(null); setMergeSelection(new Set()); toast.success('仕入先を統合しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '統合に失敗しました'),
  });

  const toggleMergeSelection = (id: string) => {
    setMergeSelection((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-h2">{t('entity.suppliers_lite', '仕入先マスタ')}</h2>
          <p className="text-sm text-neutral-sub mt-1">月次仕入先別コストの取込・集計で使う仕入先を管理します (Lite版データモデル)。</p>
        </div>
        <div className="flex gap-2">
          {mergeSelection.size >= 2 && (
            <button type="button" onClick={() => setMerging({ new_code: '', new_name: '' })}
              className="flex items-center gap-1 px-3 py-2 rounded border border-brand-red text-brand-red text-sm hover:bg-surface-sunken whitespace-nowrap">
              <GitMerge size={16} /> 選択した{mergeSelection.size}件を統合
            </button>
          )}
          <button type="button" onClick={() => setCreating({ code: '', name: '' })}
            className="flex items-center gap-1 px-3 py-2 rounded bg-brand-red text-white text-sm hover:bg-brand-red-hover whitespace-nowrap">
            <Plus size={16} /> 仕入先追加
          </button>
        </div>
      </div>

      {isLoading ? <div className="text-neutral-sub">読み込み中…</div> : (
        <div className="border border-neutral-line rounded-lg bg-white overflow-x-auto">
          <table className="w-full min-w-[480px] text-sm">
            <thead className="bg-neutral-panel text-neutral-sub">
              <tr>
                <th className="w-10 px-4 py-2 relative"><span className="sr-only">統合対象選択</span></th>
                <th className="text-left px-4 py-2 w-28">{t('field.suppliers_lite.code', 'コード')}</th>
                <th className="text-left px-4 py-2">{t('field.suppliers_lite.name', '名称')}</th>
                <th className="w-24 px-4 py-2 relative"><span className="sr-only">操作</span></th>
              </tr>
            </thead>
            <tbody>
              {suppliers.map((s) => {
                const isEditing = editing?.id === s.id;
                return (
                  <tr key={s.id} className="border-t border-neutral-line">
                    <td className="px-4 py-2">
                      {!s.is_system && (
                        <input type="checkbox" aria-label={`${s.name}を統合対象に選択`}
                          checked={mergeSelection.has(s.id)} onChange={() => toggleMergeSelection(s.id)} />
                      )}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-neutral-sub">{s.code}</td>
                    <td className="px-4 py-2">
                      {isEditing ? (
                        <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                          className="w-full px-2 py-1 rounded border border-neutral-line focus-ring" />
                      ) : (
                        <>
                          {s.name}
                          {s.is_system && <span className="ml-2 text-xs text-neutral-sub">(システム定義)</span>}
                        </>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      {s.is_system ? null : isEditing ? (
                        <>
                          <button type="button" aria-label="保存" onClick={() => updateMut.mutate(editing)}
                            disabled={updateMut.isPending || editing.name.trim() === ''}
                            className="p-1 text-brand-red hover:text-brand-red-hover disabled:opacity-40"><Save size={14} /></button>
                          <button type="button" aria-label="キャンセル" onClick={() => setEditing(null)}
                            className="ml-1 p-1 text-neutral-sub hover:text-neutral-text"><X size={14} /></button>
                        </>
                      ) : (
                        <>
                          <button type="button" aria-label="編集"
                            onClick={() => setEditing({ id: s.id, name: s.name })}
                            className="p-1 text-brand-red hover:text-brand-red-hover"><Pencil size={14} /></button>
                          <button type="button" aria-label="削除" onClick={() => setDeleting(s)}
                            className="ml-1 p-1 text-neutral-sub hover:text-warning-text"><Trash2 size={14} /></button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {suppliers.length === 0 && <EmptyState message="仕入先が登録されていません" description="「仕入先追加」から追加してください" />}
        </div>
      )}

      <Modal
        open={creating !== null}
        title="仕入先追加"
        onClose={() => setCreating(null)}
        size="sm"
        footer={creating && (
          <>
            <button type="button" onClick={() => setCreating(null)} className="px-4 py-2 rounded border border-neutral-line hover:bg-neutral-panel">キャンセル</button>
            <button type="submit" form="supplier-create-form" disabled={createMut.isPending} className="px-4 py-2 rounded bg-brand-red text-white hover:bg-brand-red-hover disabled:opacity-50">
              {createMut.isPending ? '作成中…' : '作成'}
            </button>
          </>
        )}
      >
        {creating && (
          <form id="supplier-create-form"
            onSubmit={(e) => {
              e.preventDefault();
              const code = creating.code.trim();
              const name = creating.name.trim();
              if (!code) { toast.error('コードを入力してください'); return; }
              if (!name) { toast.error('名称を入力してください'); return; }
              createMut.mutate({ code, name });
            }}
            className="space-y-3">
            <div>
              <label className="block text-sm text-neutral-sub mb-1">{t('field.suppliers_lite.code', 'コード')}*</label>
              <input required autoFocus value={creating.code} onChange={(e) => setCreating({ ...creating, code: e.target.value })}
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
            </div>
            <div>
              <label className="block text-sm text-neutral-sub mb-1">{t('field.suppliers_lite.name', '名称')}*</label>
              <input required value={creating.name} onChange={(e) => setCreating({ ...creating, name: e.target.value })}
                placeholder="例：〇〇食品株式会社" className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
            </div>
          </form>
        )}
      </Modal>

      <Modal
        open={merging !== null}
        title={`仕入先の統合 (${mergeSelection.size}件)`}
        onClose={() => setMerging(null)}
        size="sm"
        footer={merging && (
          <>
            <button type="button" onClick={() => setMerging(null)} className="px-4 py-2 rounded border border-neutral-line hover:bg-neutral-panel">キャンセル</button>
            <button type="submit" form="supplier-merge-form" disabled={mergeMut.isPending} className="px-4 py-2 rounded bg-brand-red text-white hover:bg-brand-red-hover disabled:opacity-50">
              {mergeMut.isPending ? '統合中…' : '統合する'}
            </button>
          </>
        )}
      >
        {merging && (
          <form id="supplier-merge-form"
            onSubmit={(e) => {
              e.preventDefault();
              const new_code = merging.new_code.trim();
              const new_name = merging.new_name.trim();
              if (!new_code || !new_name) { toast.error('統合先のコードと名称を入力してください'); return; }
              mergeMut.mutate({ source_ids: [...mergeSelection], new_code, new_name });
            }}
            className="space-y-3">
            <p className="text-sm text-neutral-sub">
              選択した {mergeSelection.size} 件の仕入先を、新しい1つの仕入先に統合します。
              過去データは統合先の仕入先に集計されます。この操作は元に戻せません。
            </p>
            <div>
              <label className="block text-sm text-neutral-sub mb-1">統合先コード*</label>
              <input required autoFocus value={merging.new_code} onChange={(e) => setMerging({ ...merging, new_code: e.target.value })}
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
            </div>
            <div>
              <label className="block text-sm text-neutral-sub mb-1">統合先名称*</label>
              <input required value={merging.new_name} onChange={(e) => setMerging({ ...merging, new_name: e.target.value })}
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
            </div>
          </form>
        )}
      </Modal>

      <ConfirmDialog
        open={deleting !== null}
        title="仕入先を削除しますか？"
        destructive
        confirmLabel="削除する"
        loading={deleteMut.isPending}
        onClose={() => { if (!deleteMut.isPending) setDeleting(null); }}
        onConfirm={() => { if (deleting) deleteMut.mutate(deleting.id); }}
        message={<p className="text-sm">過去データは維持されます (アーカイブ扱い)。集計には引き続き含まれます。</p>}
      />
    </div>
  );
}
